import { axiosInstance } from './api';
import type { WeeklyReportSections } from './weeklyReports';
import type { TableState } from './dashboardSummary';

export type ZoneHealth = 'red' | 'yellow' | 'green' | 'unknown';

export interface PortfolioZoneDto {
  zone_id: number;
  zone_name: string;
  /** Zone.type — boards mirror the branch selector's retail-only filter.
   *  Absent on pre-flag BE responses (treat as retail). */
  zone_type?: string | null;
  tasks: { overdue: number; pending: number };
  dirty_table_count: number | null;
  layout_health: ZoneHealth | null;
  queue_health: ZoneHealth | null;
  overall_health: ZoneHealth;
}

export const getPortfolioSummary = () =>
  axiosInstance.get<PortfolioZoneDto[]>('/core/api/portfolio/');

// ── Per-unit dashboard batch (portfolio v2) ─────────────────────────────────
// One request feeds the whole «Все филиалы» dashboard: per-zone unit statuses
// for the biathlon dot rows + stored crop URLs for problem photos/popovers.
// Photos are the ALREADY-STORED crops (no minting) — freshness matches the
// regular dashboard's stored-crop fallback.

export interface PortfolioTaskDto {
  id: number | string;
  name: string;
  status: 'created' | 'pending' | 'pre_deadline' | 'overdue' | 'done';
  due_at: string | null;
}

export interface PortfolioRackDto {
  id: number | string;
  name: string;
  rack_type: string | null;
  status: 'green' | 'yellow' | 'orange' | 'grey' | 'unknown';
  image_url: string | null;
  captured_at: string | null;
  /** ISO the current non-green issue started; null when the rack is OK. */
  issue_since?: string | null;
}

export interface PortfolioTableDto {
  id: number | string;
  name: string;
  dirty: boolean | null;
  state?: TableState;
  recheck_pending?: boolean;
  /** Статус «состояние нельзя определить»: причина, null/absent = статуса нет. */
  spoiled_reason?: 'several_tables' | 'obstructed' | 'glare' | null;
  image_url: string | null;
  captured_at: string | null;
}

export interface PortfolioQueueDto {
  id: number | string;
  name: string;
  status: 'red' | 'yellow' | 'green' | 'unknown';
  current: number | null;
  max_allowed: number;
  image_url: string | null;
  captured_at: string | null;
}

export interface PortfolioZoneDashboardDto {
  zone_id: number;
  zone_name: string;
  /** False = the whole location produced no frames this zone-local day —
   *  surface the "on-site computer offline" banner (unless closed). */
  has_frames_today: boolean;
  latest_frame_at: string | null;
  /** Working-hours gate: closed zones are legitimately dark, not broken. */
  is_open: boolean | null;
  opens_at?: string | null;
  /** Zone.type — see PortfolioZoneDto.zone_type. */
  zone_type?: string | null;
  /** Today's zone-local window only (not historical accumulation). */
  tasks: { overdue: number; pending: number };
  /** Today's individual tasks (ALL statuses incl. done), due_at order — the
   *  per-task biathlon dots + carousel task tile. Absent on pre-flag BE. */
  tasks_items?: PortfolioTaskDto[];
  /** Branch has ≥1 active checklist item — the tasks component renders only
   *  when true. Absent on pre-flag BE responses (treat as true). */
  checklists_configured?: boolean;
  /** Zone runs an on-site computer (vs router/SHARED capture) — picks the
   *  no-data wording. Absent on pre-flag BE responses. */
  has_local_computer?: boolean;
  racks: PortfolioRackDto[];
  tables: PortfolioTableDto[];
  queues: PortfolioQueueDto[];
}

// Data-level pagination: the BE computes at most 20 zones per request, so the
// caller MUST scope every call to the page of branches actually on screen.
export const getPortfolioDashboard = (zoneIds: Array<string | number>) =>
  axiosInstance.get<PortfolioZoneDashboardDto[]>('/core/api/portfolio/dashboard/', {
    params: { zone_ids: zoneIds.join(',') },
    timeout: 30_000,
  });

// ── Report mini-card batch ──────────────────────────────────────────────────
// Light per-zone digest for the «Все филиалы» report view. Day: category
// traffic lights + the day's headline photos lifted from the STORED daily
// report (no recomputation). Week: the stored weekly report's sections.

export type TrafficStatus = 'red' | 'yellow' | 'green';

export interface PortfolioReportPhotoDto {
  kind: 'queue' | 'layout_drop' | 'table';
  image_url: string;
  caption: string;
  at: string | null;
}

export interface PortfolioZoneReportDto {
  zone_id: number;
  zone_name: string;
  /** Zone was closed that day (weekday mask) — quiet "Закрыто" state. */
  closed: boolean;
  report_exists: boolean;
  /** Day only, when no report exists yet: "ongoing" = the working day is still
   *  logging (verdict not final), "forming" = day closed, report generates next
   *  morning. Null otherwise. Drives the «сегодня ещё идёт» / «отчёт
   *  формируется» labels — the BE never live-computes today's verdict. */
  today_status?: 'ongoing' | 'forming' | null;
  /** Day only: per rack-category traffic light («Выпечка жёлтый…») with the
   *  day-findings facts for hover (null when the category had no issues). */
  layout: {
    rack_type: string;
    status: TrafficStatus;
    shortage_pct?: number | null;
    problem_hours?: number | null;
  }[] | null;
  /** Day only: aggregate open/close layout fill, when cheaply available. */
  layout_open_close?: { open_pct: number | null; close_pct: number | null } | null;
  queue: {
    status: TrafficStatus;
    peak_people: number | null;
    peak_at: string | null;
  } | null;
  tables: {
    episodes: number | null;
    longest_min: number | null;
    longest_table?: string | null;
    error?: string | null;
    no_data?: boolean;
  } | null;
  tasks: { done: number; total: number; overdue: number } | null;
  photos: PortfolioReportPhotoDto[];
  /** period=week only: stored weekly sections (FE derives the lights). */
  week?: Partial<WeeklyReportSections> | null;
}

export const getPortfolioReport = (period: 'day' | 'week', date: string, zoneIds: Array<string | number>) =>
  axiosInstance.get<PortfolioZoneReportDto[]>('/core/api/portfolio/report/', {
    params: { period, date, zone_ids: zoneIds.join(',') },
    timeout: 30_000,
  });
